import React, { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import usePaginatedResource from '../../../hooks/usePaginatedResource';
import useResourceMutation from '../../../hooks/useResourceMutation';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import DataTable, { Column } from '../../../components/shared/DataTable/DataTable';
import Modal from '../../../components/shared/Modal/Modal';
import ConfirmDialog from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import Button from '../../../components/shared/Button/Button';
import FormField from '../../../components/shared/FormField/FormField';
import Input from '../../../components/shared/Input/Input';
import SearchableSelect from '../../../components/shared/SearchableSelect/SearchableSelect';
import { createDriver, deleteDriver, updateDriver } from '../../../services/drivers';
import { listUsers } from '../../../services/accounts';
import { listVehicles } from '../../../services/vehicles';
import type { Driver, User, Vehicle } from '../../../types/api';

interface FormState {
  user: string;
  license_number: string;
  license_expiry: string;
  vehicle: string;
}

const empty: FormState = { user: '', license_number: '', license_expiry: '', vehicle: '' };

const Drivers: React.FC = () => {
  const { data: drivers, loading, refetch } = usePaginatedResource<Driver>('/drivers/');

  const [editing, setEditing] = useState<Driver | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(empty);
  const [deleteTarget, setDeleteTarget] = useState<Driver | null>(null);

  // Driver users (by name) + vehicles (by plate) for the searchable pickers.
  const [driverUsers, setDriverUsers] = useState<User[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  useEffect(() => {
    listUsers({ role: 'driver', page_size: 1000 }).then(setDriverUsers).catch(() => setDriverUsers([]));
    listVehicles({ page_size: 1000 }).then(setVehicles).catch(() => setVehicles([]));
  }, []);
  const userOptions = useMemo(
    () => driverUsers.map((u) => ({
      value: String(u.id),
      label: [`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim(), u.phone_number]
        .filter(Boolean).join(' · ') || `User #${u.id}`,
    })),
    [driverUsers],
  );
  const vehicleOptions = useMemo(
    () => [
      { value: '', label: 'Unassigned' },
      ...vehicles.map((v) => ({
        value: String(v.id),
        label: `${v.registration_number} — ${v.capacity} seats`,
      })),
    ],
    [vehicles],
  );

  const save = useResourceMutation(
    async (state: FormState) => {
      const payload = {
        user: parseInt(state.user, 10),
        license_number: state.license_number,
        license_expiry: state.license_expiry,
        vehicle: state.vehicle ? parseInt(state.vehicle, 10) : null,
      };
      return editing
        ? updateDriver(editing.id, payload)
        : createDriver(payload);
    },
    { successMessage: editing ? 'Driver updated' : 'Driver created' }
  );

  const del = useResourceMutation(
    (id: number) => deleteDriver(id),
    { successMessage: 'Driver deleted' }
  );

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setShowForm(true);
  };

  const openEdit = (d: Driver) => {
    setEditing(d);
    setForm({
      user: String(d.user),
      license_number: d.license_number,
      license_expiry: d.license_expiry,
      vehicle: d.vehicle ? String(d.vehicle) : '',
    });
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await save.run(form);
      setShowForm(false);
      setForm(empty);
      setEditing(null);
      refetch();
    } catch { /* toast */ }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await del.run(deleteTarget.id);
      setDeleteTarget(null);
      refetch();
    } catch { /* toast */ }
  };

  const columns: Column<Driver>[] = [
    { key: 'full_name', header: 'Driver', sortable: true,
      render: (d) => d.full_name ?? `User #${d.user}` },
    { key: 'phone', header: 'Phone', render: (d) => d.phone ?? '—' },
    { key: 'license_number', header: 'License #' },
    { key: 'license_expiry', header: 'License expiry', sortable: true,
      render: (d) => new Date(d.license_expiry).toLocaleDateString() },
    { key: 'vehicle', header: 'Vehicle',
      render: (d) => d.vehicle == null ? '—' : `#${d.vehicle}` },
    { key: 'is_available', header: 'Available',
      render: (d) => (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
          d.is_available ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
        }`}>
          <span className={`h-1.5 w-1.5 rounded-full ${d.is_available ? 'bg-emerald-500' : 'bg-slate-400'}`} aria-hidden />
          {d.is_available ? 'Available' : 'Unavailable'}
        </span>
      ) },
  ];

  const actions = (d: Driver) => (
    <div className="inline-flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={() => openEdit(d)}>Edit</Button>
      <Button variant="danger" size="sm" onClick={() => setDeleteTarget(d)}>Delete</Button>
    </div>
  );

  return (
    <>
      <PageHeader
        title="Drivers"
        description="Manage driver records and license details."
        actions={
          <Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>
            New driver
          </Button>
        }
      />

      <DataTable<Driver>
        columns={columns}
        data={drivers}
        rowKey="id"
        loading={loading}
        emptyTitle="No drivers yet"
        emptyDescription="Add your first driver to start scheduling trips."
        emptyAction={
          <Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>
            New driver
          </Button>
        }
        actions={actions}
      />

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? 'Edit driver' : 'New driver'}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowForm(false)} disabled={save.loading}>Cancel</Button>
            <Button onClick={handleSave} loading={save.loading}
              disabled={!form.user || !form.license_number || !form.license_expiry}>
              {editing ? 'Save changes' : 'Create driver'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSave} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Driver" name="user" required error={save.fieldErrors.user}
                     hint="User with role=driver">
            <SearchableSelect
              id="user"
              options={userOptions}
              value={form.user}
              onChange={(v) => setForm({ ...form, user: v })}
              placeholder="Select driver…"
              searchPlaceholder="Search by name…"
              aria-label="Driver"
            />
          </FormField>
          <FormField label="License number" name="license_number" required error={save.fieldErrors.license_number}>
            <Input id="license_number" required value={form.license_number}
              onChange={(e) => setForm({ ...form, license_number: e.target.value })} />
          </FormField>
          <FormField label="License expiry" name="license_expiry" required error={save.fieldErrors.license_expiry}>
            <Input id="license_expiry" type="date" required value={form.license_expiry}
              onChange={(e) => setForm({ ...form, license_expiry: e.target.value })} />
          </FormField>
          <FormField label="Vehicle" name="vehicle" hint="Optional — by number plate" error={save.fieldErrors.vehicle}>
            <SearchableSelect
              id="vehicle"
              options={vehicleOptions}
              value={form.vehicle}
              onChange={(v) => setForm({ ...form, vehicle: v })}
              placeholder="Select vehicle…"
              searchPlaceholder="Search by registration…"
              aria-label="Vehicle"
            />
          </FormField>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete driver?"
        message={`Remove driver record for ${deleteTarget?.full_name ?? `user #${deleteTarget?.user}`}? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={del.loading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default Drivers;
